import React, { useEffect } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import { CheckSolidIcon } from "@icons/CheckSolidIcon";
import { CircleIcon } from "@icons/CircleLeft";
import { PostPollClosedAt } from "../../PostPollClosedAt";
import { usePollsState } from "../../usePollsState";
import { usePollIsOpenState } from "../../usePollIsOpenState";
import { BasePollType } from "../PostPollFactory";
import { PostPollImageOption } from "./PostPollImageOption";
import { ImagePollOptionContainerStack } from "../../PostPollOptions.styles";

type PostPollTextSectionInterface = BasePollType & {
    allowChangeAnswer: boolean;
    showSupportingText: boolean;
};

export function PostPollImageSection({
    pollClosedAt,
    pollOptionHandler,
    pollOptions: initialPollOptions,
    postId,
    allowChangeAnswer,
    showSupportingText,
}: PostPollTextSectionInterface) {
    const { open } = usePollIsOpenState(pollClosedAt);
    const {
        totalResponseCount,
        handlePollOptionClick,
        pollOptions,
        hasParticipated,
        userOptionId,
        resetPollOptions,
    } = usePollsState(initialPollOptions, pollOptionHandler, postId);
    useEffect(() => {
        resetPollOptions(initialPollOptions);
    }, [initialPollOptions]);
    const allowVote = open && (allowChangeAnswer || !hasParticipated);

    return (
        <Stack gap={"8px"}>
            <ImagePollOptionContainerStack
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {pollOptions.map((pollOption, key) => {
                    const isRespondedByCurrentUser =
                        pollOption.id === userOptionId;
                    const votePercentage = totalResponseCount
                        ? Math.round(
                              (pollOption.responsesCount * 100) /
                                  totalResponseCount,
                          )
                        : 0;

                    return (
                        <Box
                            key={key}
                            style={{
                                aspectRatio: "1/1",
                                flex: "1",
                                ...(pollOptions.length === 4 && {
                                    minWidth: "40%",
                                }),
                            }}
                        >
                            <PostPollImageOption
                                pollOption={pollOption}
                                handlePollOptionClick={handlePollOptionClick}
                                enableVoting={allowVote}
                                isRespondedByCurrentUser={
                                    isRespondedByCurrentUser
                                }
                                optionPercentage={votePercentage}
                                icon={getOptionIcon(
                                    isRespondedByCurrentUser,
                                    !allowVote && !isRespondedByCurrentUser,
                                )}
                                showSupportingText={showSupportingText}
                            />
                        </Box>
                    );
                })}
            </ImagePollOptionContainerStack>
            <PostPollClosedAt pollClosedAt={pollClosedAt} />
        </Stack>
    );
}

function getOptionIcon(isRespondedByCurrentUser: boolean, inActive: boolean) {
    if (isRespondedByCurrentUser) {
        return (
            <IconButton
                sx={{
                    padding: 0,
                    backgroundColor: "#50739D",
                    width: "24px",
                    height: "24px",
                    "&:hover": {
                        backgroundColor: "#50739D",
                    },
                }}
                disableRipple
            >
                <CheckSolidIcon
                    color="var(--community-color-snow-white)"
                    width="12px"
                    height="12px"
                />
            </IconButton>
        );
    }

    return (
        <IconButton
            sx={{
                backgroundColor: "var(--community-color-snow-white)",
                width: "24px",
                height: "24px",
                padding: 0,
                "&:hover": {
                    backgroundColor: "var(--community-color-snow-white)",
                },
            }}
            disableRipple
        >
            <CircleIcon
                color={
                    inActive
                        ? "var(--community-color-dark-gray)"
                        : "var(--community-color-transparent-black-50)"
                }
            />
        </IconButton>
    );
}
